// src/components/sidebar/UserMenu.tsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Alert,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
} from '@mui/material';
import {
  Person,
  Settings,
  PrivacyTip,
  Logout,
  AccountCircle,
  Login,
  PersonAdd,
  Help,
  TravelExplore,
  Security,
  Policy,
  Psychology,
  AutoAwesome,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
// AuthModal is now mounted globally via <AuthModalHost> in App.tsx.
// We dispatch open via useAuthStore().openAuthModal() instead of
// owning a local instance.
import { Settings as SettingsPage } from '../../pages/Settings';
import { UserProfile } from '../profile/UserProfile';
import { useT } from '../../i18n/useT';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';

export const UserMenu: React.FC = () => {
  const t = useT();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showGDPRDialog, setShowGDPRDialog] = useState(false);
  const { user, profile, authMode, signOut, updateGDPRConsent, openAuthModal } =
    useAuthStore();
  const navigate = useNavigate();

  // Phase 3.6 — Memory log is gated on a per-user JWT claim
  // (`features.memoryLog`). Hide the menu entry entirely for users
  // who don't have it. The route itself also redirects when
  // unclaimed, and Firestore rules return permission-denied when a
  // client tries to read curator_decisions without it. Three
  // layers because the data is sensitive (full curator reasoning,
  // user PII derived inferences) and we want failure-mode
  // redundancy: a UI bug shouldn't be able to leak access by
  // itself.
  const { features: featureFlags } = useFeatureAccess();

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    await signOut();
    handleMenuClose();
  };

  const handleShowLogin = () => {
    // Close the dropdown menu BEFORE opening the modal — MUI's
    // focus restoration on Menu close can otherwise steal focus
    // from a Dialog opened in the same tick. We dispatch through
    // the store now (rather than local state) so the modal is
    // mounted globally and survives the Menu/Drawer unmounting
    // when the OAuth popup later steals focus.
    handleMenuClose();
    setTimeout(() => openAuthModal('login'), 0);
  };

  const handleShowSignup = () => {
    handleMenuClose();
    setTimeout(() => openAuthModal('signup'), 0);
  };

  const handleShowSettings = () => {
    setShowSettingsDialog(true);
    handleMenuClose();
  };

  const handleShowProfile = () => {
    setShowProfileDialog(true);
    handleMenuClose();
  };

  const handleShowGDPR = () => {
    setShowGDPRDialog(true);
    handleMenuClose();
  };

  const handlePrivacyClick = () => {
    navigate('/privacy');
    handleMenuClose();
  };

  const handleShowCuratorLog = () => {
    navigate('/curator-log');
    handleMenuClose();
  };

  const handleShowCollections = () => {
    navigate('/collections');
    handleMenuClose();
  };

  const handleUpdateGDPRConsent = async (updates: any) => {
    try {
      await updateGDPRConsent(updates);
      // Dialog will close automatically when profile is updated
    } catch (error) {
      console.error('Failed to update GDPR consent:', error);
    }
  };

  const getDisplayName = () => {
    if (profile?.displayName) return profile.displayName;
    if (profile?.email) return profile.email;
    return t('Guest User');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Check if travel profile is complete
  const hasCompleteProfile = profile?.travelProfile?.completedAt !== undefined;
  const hasPartialProfile = profile?.travelProfile && !hasCompleteProfile;

  // Check if user needs to update GDPR consent (if policy version changed)
  const needsGDPRUpdate = profile?.gdprConsent?.privacyPolicyVersion !== '3.0';

  const GDPRConsentDialog = () => {
    const [tempConsent, setTempConsent] = useState(profile?.gdprConsent || {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    });

    const handleConsentChange = (category: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setTempConsent(prev => ({
        ...prev,
        [category]: event.target.checked,
      }));
    };

    const handleSave = () => {
      handleUpdateGDPRConsent({
        ...tempConsent,
        consentDate: new Date(),
        privacyPolicyVersion: '3.0',
      });
      setShowGDPRDialog(false);
    };

    return (
      <Dialog
        open={showGDPRDialog}
        onClose={() => setShowGDPRDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5">{t("Data & Privacy Preferences")}</Typography>
          <Typography variant="body2" color="text.secondary">
            
            {t("Manage your data processing and privacy preferences")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {needsGDPRUpdate && (
            <Alert severity="info" sx={{ mb: 3 }}>
              
              {t("Our Privacy Policy has been updated. Please review and update your consent preferences.")}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              
              {t("Data Processing Consent")}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              
              {t("Choose which types of data processing you consent to. You can change these preferences at any time.")}
            </Typography>

            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempConsent.necessary}
                      disabled // Always required
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        
                        {t("Necessary Processing (Required)")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        
                        {t("Essential for service functionality, authentication, and security")}
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempConsent.functional}
                      onChange={handleConsentChange('functional')}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        
                        {t("Functional Processing")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        
                        {t("Remember your preferences, travel profile, and custom settings")}
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempConsent.analytics}
                      onChange={handleConsentChange('analytics')}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        
                        {t("Analytics Processing")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        
                        {t("Anonymous usage analytics to improve our service")}
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempConsent.marketing}
                      onChange={handleConsentChange('marketing')}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        
                        {t("Marketing Communications")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        
                        {t("Service updates and feature announcements (you can unsubscribe anytime)")}
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>

            {profile?.gdprConsent && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  
                  {t("Last updated:")} {profile.gdprConsent.consentDate.toLocaleDateString()}
                </Typography>
                {profile.gdprConsent.ageVerified && (
                  <Typography variant="body2" color="text.secondary">
                    
                    {t("Age verified:")} {profile.gdprConsent.ageVerificationDate?.toLocaleDateString()}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  
                  {t("Privacy Policy version:")} {profile.gdprConsent.privacyPolicyVersion}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowGDPRDialog(false)} variant="outlined">
            
            {t("Cancel")}
          </Button>
          <Button onClick={handleSave} variant="contained">
            
            {t("Save Preferences")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          onClick={handleMenuOpen}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            p: 1,
            borderRadius: 2,
            color: 'text.primary',
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              mr: 2,
              bgcolor: authMode === 'registered' ? 'primary.main' : 'grey.500',
              fontSize: '0.875rem',
            }}
          >
            {authMode === 'registered' ? (
              getInitials(getDisplayName())
            ) : (
              <Person fontSize="small" />
            )}
          </Avatar>
          <Box sx={{ flex: 1, textAlign: 'left' }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {getDisplayName()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {authMode === 'registered' ? t('Registered User') : t('Guest User')}
            </Typography>
          </Box>
          {needsGDPRUpdate && (
            <Badge color="warning" variant="dot" />
          )}
        </Button>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{
          sx: { width: 220, mt: -1 }
        }}
      >
        {authMode === 'anonymous' ? (
          // Anonymous user menu - separate login and signup options
          [
            <MenuItem key="login" onClick={handleShowLogin}>
              <ListItemIcon>
                <Login fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("Sign In")} />
            </MenuItem>,
            <MenuItem key="signup" onClick={handleShowSignup}>
              <ListItemIcon>
                <PersonAdd fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("Create Account")} />
            </MenuItem>,
            <Divider key="divider1" />,
            <MenuItem key="settings" onClick={handleShowSettings}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("Settings")} />
            </MenuItem>,
            <MenuItem key="privacy" onClick={handlePrivacyClick}>
              <ListItemIcon>
                <PrivacyTip fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("Privacy")} />
            </MenuItem>,
            <MenuItem key="help" onClick={handleMenuClose}>
              <ListItemIcon>
                <Help fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("Help")} />
            </MenuItem>,
          ]
        ) : (
          // Registered user menu
          [
            <MenuItem key="account" onClick={handleMenuClose}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Account" />
            </MenuItem>,
            <MenuItem key="collections" onClick={handleShowCollections}>
              <ListItemIcon>
                <AutoAwesome fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Collections" />
            </MenuItem>,
            <MenuItem key="profile" onClick={handleShowProfile}>
              <ListItemIcon>
                <Badge 
                  color="secondary" 
                  variant="dot" 
                  invisible={hasCompleteProfile}
                  sx={{
                    '& .MuiBadge-badge': {
                      bgcolor: hasPartialProfile ? 'warning.main' : 'error.main',
                    }
                  }}
                >
                  <TravelExplore fontSize="small" />
                </Badge>
              </ListItemIcon>
              <ListItemText 
                primary="Travel Profile" 
                secondary={
                  hasCompleteProfile ? 'Complete' :
                  hasPartialProfile ? 'Incomplete' : 'Not set up'
                }
              />
            </MenuItem>,
            <MenuItem key="gdpr" onClick={handleShowGDPR}>
              <ListItemIcon>
                <Badge 
                  color="warning" 
                  variant="dot" 
                  invisible={!needsGDPRUpdate}
                >
                  <Security fontSize="small" />
                </Badge>
              </ListItemIcon>
              <ListItemText 
                primary="Privacy & Data" 
                secondary={needsGDPRUpdate ? 'Update required' : 'Manage preferences'}
              />
            </MenuItem>,
            <MenuItem key="settings" onClick={handleShowSettings}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </MenuItem>,
            <MenuItem key="privacy" onClick={handlePrivacyClick}>
              <ListItemIcon>
                <PrivacyTip fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Privacy Policy" />
            </MenuItem>,
            <Divider key="divider1" />,
            <MenuItem key="help" onClick={handleMenuClose}>
              <ListItemIcon>
                <Help fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Help" />
            </MenuItem>,
            // Phase 3.6 — Memory log is gated on the `memoryLog`
            // feature claim. Spread an empty array (rather than
            // returning null/false) so React treats the omission
            // as if the items never existed in the array — keeps
            // keys stable and avoids "key already exists" warnings
            // when the flag flips during a session.
            ...(featureFlags.memoryLog
              ? [
                  <MenuItem key="curator-log" onClick={handleShowCuratorLog}>
                    <ListItemIcon>
                      <Psychology fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Memory log"
                      secondary="See what I'm learning about you"
                    />
                  </MenuItem>,
                  <Divider key="divider2" />,
                ]
              : [<Divider key="divider2-no-memlog" />]),
            <MenuItem key="signout" onClick={handleSignOut}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Sign Out" />
            </MenuItem>,
          ]
        )}
      </Menu>

      {/* Travel Profile Dialog */}
      <Dialog 
        open={showProfileDialog} 
        onClose={() => setShowProfileDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <UserProfile onClose={() => setShowProfileDialog(false)} />
        </DialogContent>
      </Dialog>

      {/* GDPR Consent Dialog */}
      <GDPRConsentDialog />

      {/* Settings Dialog */}
      <Dialog 
        open={showSettingsDialog} 
        onClose={() => setShowSettingsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle>
          <Typography variant="h5">{t("Settings")}</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <SettingsPage />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowSettingsDialog(false)} variant="outlined">
            
            {t("Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
